// Portfolio › Thinking (FOUNDER-OS; 12 §What lives in it 1, 2, 6): the way into the founder's own
// spaces in Founder OS — the Thinking space (journal, ideas, strategy pages), the decision log
// board, every doc, the portfolio vision and the portfolio meeting. Plain links into Work and
// Cadence of the portfolio company: the same modules, one level up.
import { LocationHeader } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BookOpenIcon,
  CalendarCheckIcon,
  CompassIcon,
  FolderIcon,
  LightbulbIcon,
  ListChecksIcon,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { boardsQuery, spacesQuery } from "../work/work-queries";

function Tile({
  icon: Icon,
  title,
  description,
  link,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  link: ReactNode;
}) {
  return (
    <li className="relative flex gap-3 rounded-lg border border-border bg-card p-4 shadow-xs hover:border-strong">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-secondary text-faint">
        <Icon aria-hidden="true" className="size-[18px]" />
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-semibold text-foreground">{link ?? title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </li>
  );
}

const stretch = "after:absolute after:inset-0 after:rounded-lg hover:underline";

export function PortfolioThinkingScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const slug = company.slug;
  const spaces = useQuery(spacesQuery(api, company.id));
  const boards = useQuery(boardsQuery(api, company.id));
  const thinking = spaces.data?.data.find((s) => s.name === "Thinking");
  const decisions = boards.data?.data.find((b) => b.name === "Decision log");
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="portfolio-thinking">
      <LocationHeader
        icon={LightbulbIcon}
        crumbs={[
          { label: "Portfolio", render: <Link to="/c/$slug/portfolio" params={{ slug }} /> },
        ]}
        title="Thinking"
      />
      <div className="flex min-w-0 flex-col gap-3 px-6 py-5 max-md:px-4">
        <p className="max-w-prose text-sm text-muted-foreground">
          Your own space, apart from the teams: map your thoughts, keep the decision log, set the
          vision and run the portfolio meeting. Nobody in the companies sees it.
        </p>
        <ul className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-3">
          <Tile
            icon={FolderIcon}
            title="Thinking space"
            description="Journal, ideas and strategy pages."
            link={
              thinking ? (
                <Link
                  to="/c/$slug/work/s/$spaceId"
                  params={{ slug, spaceId: thinking.id }}
                  className={stretch}
                >
                  Thinking space
                </Link>
              ) : (
                <Link to="/c/$slug/work" params={{ slug }} className={stretch}>
                  Thinking space
                </Link>
              )
            }
          />
          <Tile
            icon={ListChecksIcon}
            title="Decision log"
            description="Context, options, the decision and why — with a review date."
            link={
              <Link
                {...(decisions
                  ? { to: "/c/$slug/work/b/$boardId", params: { slug, boardId: decisions.id } }
                  : { to: "/c/$slug/work", params: { slug } })}
                className={stretch}
              >
                Decision log
              </Link>
            }
          />
          <Tile
            icon={BookOpenIcon}
            title="Docs"
            description="Every page you wrote in Founder OS."
            link={
              <Link to="/c/$slug/work/docs" params={{ slug }} className={stretch}>
                Docs
              </Link>
            }
          />
          <Tile
            icon={CompassIcon}
            title="Portfolio vision"
            description="Values, focus, the 10-year target, the 3-year picture and the 1-year plan."
            link={
              <Link to="/c/$slug/kpis/vision" params={{ slug }} className={stretch}>
                Portfolio vision
              </Link>
            }
          />
          <Tile
            icon={CalendarCheckIcon}
            title="Portfolio meeting"
            description="Built from the misses, off-track priorities and escalations."
            link={
              <Link to="/c/$slug/kpis/meetings" params={{ slug }} className={stretch}>
                Portfolio meeting
              </Link>
            }
          />
        </ul>
      </div>
    </div>
  );
}
